import { useTranslation } from "@sagot/i18n";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, Icon } from "@sagot/ui";
import { useState } from "react";
import { useSavedReplies } from "./inbox-api";

/** Fills `{{name}}` placeholders of a saved reply from known values; unknown ones stay as typed. */
export function fillTemplate(body: string, values: Readonly<Record<string, string>>): string {
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) => values[key] ?? match);
}

interface SavedRepliesMenuProps {
  disabled: boolean;
  values: Readonly<Record<string, string>>;
  onInsert: (text: string) => void;
}

export function SavedRepliesMenu({ disabled, values, onInsert }: SavedRepliesMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const replies = useSavedReplies(open);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" disabled={disabled}>
          <Icon name="scroll-text" />
          {t("engage.inbox.composer.savedReplies")}
          <Icon name="chevron-down" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-80">
        <DropdownMenuLabel>{t("engage.inbox.composer.savedReplies")}</DropdownMenuLabel>
        {replies.data?.length === 0 ? <div className="px-2.5 py-2 text-xs text-fg-subtle">{t("engage.inbox.composer.noSavedReplies")}</div> : null}
        {(replies.data ?? []).map((reply) => {
          const text = fillTemplate(reply.body, values);
          return (
            <DropdownMenuItem key={reply.id} className="flex-col items-start gap-0" onSelect={() => { onInsert(text); }}>
              <span className="flex w-full items-center gap-2 text-sm font-semibold">
                {reply.title}
                {reply.shortcut ? <span className="ml-auto font-mono text-[11px] font-normal text-fg-subtle">{reply.shortcut}</span> : null}
              </span>
              <span className="block w-full truncate text-xs text-fg-subtle">{text}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
